import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { 
  PlusCircle, 
  Search, 
  Filter,
  Eye,
  Edit3,
  Pause,
  Play,
  Trash2,
  MoreHorizontal,
  TrendingUp,
  MapPin,
  DollarSign,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';

const listings = [
  { 
    id: 'LIST-001', 
    title: 'USD Cash - Best Rates in Lagos', 
    type: 'sell',
    currency: 'USD',
    rate: 1550,
    minAmount: 100,
    maxAmount: 10000,
    status: 'active',
    views: 245,
    inquiries: 12,
    trades: 8,
    location: 'Lekki, Lagos',
    created: '2 days ago'
  },
  { 
    id: 'LIST-002', 
    title: 'Buying EUR at Premium Rate', 
    type: 'buy',
    currency: 'EUR',
    rate: 1680,
    minAmount: 500,
    maxAmount: 5000,
    status: 'active',
    views: 189,
    inquiries: 8,
    trades: 5,
    location: 'Victoria Island, Lagos',
    created: '5 days ago'
  },
  { 
    id: 'LIST-003', 
    title: 'GBP Sterling - Instant Exchange', 
    type: 'sell',
    currency: 'GBP',
    rate: 1950,
    minAmount: 200,
    maxAmount: 3000,
    status: 'paused',
    views: 156,
    inquiries: 5,
    trades: 3,
    location: 'Ikeja, Lagos',
    created: '1 week ago'
  },
  { 
    id: 'LIST-004', 
    title: 'NGN to USD - Corporate Rates', 
    type: 'sell',
    currency: 'USD',
    rate: 1545,
    minAmount: 5000,
    maxAmount: 50000,
    status: 'active',
    views: 423,
    inquiries: 28,
    trades: 15,
    location: 'Ikoyi, Lagos',
    created: '2 weeks ago'
  },
];

export default function VendorListings() {
  const navigate = useNavigate();

  return (
    
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">My Listings</h1>
            <p className="text-muted-foreground">Manage your forex exchange listings</p>
          </div>
          <Button 
            className="bg-emerald-500 hover:bg-emerald-600"
            onClick={() => navigate('/forex/vendor/listing/new')}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Create Listing
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Listings</p>
                  <p className="text-2xl font-bold">12</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                  <p className="text-2xl font-bold">2.4K</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Inquiries</p>
                  <p className="text-2xl font-bold">48</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-violet-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed Trades</p>
                  <p className="text-2xl font-bold">156</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search listings..." className="pl-10" />
          </div>
          <Select defaultValue="all">
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>
          <Select defaultValue="all">
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="buy">Buy</SelectItem>
              <SelectItem value="sell">Sell</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Listings Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Listings</CardTitle>
            <CardDescription>Manage your forex exchange listings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Listing</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rate</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Limits</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Stats</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((listing) => (
                    <tr key={listing.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-sm font-medium">{listing.title}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {listing.location}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={cn(
                          "text-xs capitalize",
                          listing.type === 'buy' ? "bg-blue-500/20 text-blue-500" : "bg-emerald-500/20 text-emerald-500"
                        )}>
                          {listing.type}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm font-medium">
                        ₦{listing.rate.toLocaleString()}/{listing.currency}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        ${listing.minAmount.toLocaleString()} - ${listing.maxAmount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {listing.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            {listing.inquiries}
                          </span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {listing.trades}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={cn(
                          "text-xs capitalize",
                          listing.status === 'active' ? "bg-emerald-500/20 text-emerald-500" : "bg-amber-500/20 text-amber-500"
                        )}>
                          {listing.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => navigate(`/forex/listing/${listing.id}`)}
                            title="View Listing"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => navigate(`/forex/vendor/listing/edit/${listing.id}`)}
                            title="Edit Listing"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                toast({
                                  title: listing.status === 'active' ? 'Listing Paused' : 'Listing Activated',
                                  description: `${listing.title} has been ${listing.status === 'active' ? 'paused' : 'activated'}.`,
                                });
                              }}>
                                {listing.status === 'active' ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                                {listing.status === 'active' ? 'Pause' : 'Activate'}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-500"
                                onClick={() => {
                                  toast({
                                    title: 'Listing Deleted',
                                    description: `${listing.title} has been deleted.`,
                                    variant: 'destructive',
                                  });
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    
  );
}
